import { Button, Input, Label, cn } from '@recipes/ui';
import { Bell, BellOff, Pause, Play, Plus, TimerIcon, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { announce } from '@/components/feedback/live-region';
import {
  formatRemaining,
  notificationPermission,
  remainingMs,
  requestNotificationPermission,
  timerCue,
  timerStore,
  useTimers,
  type CookingTimer,
} from '@/lib/timers/timer-store';

/** Mount once: plays the cue and announces when any timer finishes. */
export function TimerCueWatcher() {
  const { t } = useTranslation();
  useEffect(
    () =>
      timerStore.onDone((timer) => {
        timerCue(timer, {
          title: t('cooking.timers.doneTitle'),
          body: t('cooking.timers.doneBody', { label: timer.label }),
        });
        announce(t('cooking.timers.doneBody', { label: timer.label }), 'assertive');
      }),
    [t],
  );
  return null;
}

function TimerRow({ timer, large }: { timer: CookingTimer; large?: boolean }) {
  const { t } = useTranslation();
  const remaining = remainingMs(timer);
  const done = timer.status === 'DONE';
  const progress = timer.durationSeconds > 0 ? 1 - remaining / (timer.durationSeconds * 1000) : 1;
  return (
    <li
      className={cn(
        'rounded-lg border p-3',
        done && 'border-warning bg-warning/20',
        large && 'p-4',
      )}
      data-testid="timer-item"
      data-timer-status={timer.status}
    >
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className={cn('truncate font-medium', large && 'text-lg')}>{timer.label}</p>
          <p
            className={cn('font-mono tabular-nums', large ? 'text-4xl' : 'text-2xl')}
            aria-live={done ? 'assertive' : 'off'}
            data-testid="timer-remaining"
          >
            {done ? t('cooking.timers.done') : formatRemaining(remaining)}
          </p>
        </div>
        <div className="flex flex-wrap justify-end gap-1">
          {done ? (
            <Button
              size={large ? 'lg' : 'default'}
              onClick={() => timerStore.acknowledge(timer.id)}
              data-testid="timer-dismiss"
            >
              <BellOff aria-hidden /> {t('cooking.timers.dismiss')}
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                size={large ? 'icon-lg' : 'icon'}
                aria-label={
                  timer.status === 'RUNNING'
                    ? t('cooking.timers.pause')
                    : t('cooking.timers.resume')
                }
                onClick={() =>
                  timer.status === 'RUNNING'
                    ? timerStore.pause(timer.id)
                    : timerStore.resume(timer.id)
                }
                data-testid="timer-toggle"
              >
                {timer.status === 'RUNNING' ? <Pause aria-hidden /> : <Play aria-hidden />}
              </Button>
              <Button
                variant="outline"
                size={large ? 'lg' : 'default'}
                onClick={() => timerStore.addSeconds(timer.id, 60)}
                aria-label={t('cooking.timers.addMinute')}
                data-testid="timer-add-minute"
              >
                <Plus aria-hidden /> {t('cooking.timers.oneMinute')}
              </Button>
            </>
          )}
          <Button
            variant="ghost"
            size={large ? 'icon-lg' : 'icon'}
            aria-label={t('cooking.timers.cancel')}
            onClick={() => timerStore.cancel(timer.id)}
            data-testid="timer-cancel"
          >
            <X aria-hidden />
          </Button>
        </div>
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted" aria-hidden>
        <div
          className="h-full bg-primary transition-[width] motion-reduce:transition-none"
          style={{ width: `${Math.min(100, Math.max(0, progress * 100))}%` }}
        />
      </div>
    </li>
  );
}

export interface TimerPanelProps {
  recipeId?: string | null;
  large?: boolean;
  className?: string;
}

/** Global timer list with a quick "new timer" form and notification permission toggle. */
export function TimerPanel({ recipeId, large, className }: TimerPanelProps) {
  const { t } = useTranslation();
  const timers = useTimers();
  const [label, setLabel] = useState('');
  const [minutes, setMinutes] = useState('10');
  const [permission, setPermission] = useState(notificationPermission());

  const start = () => {
    const mins = Number(minutes);
    if (!Number.isFinite(mins) || mins <= 0) return;
    timerStore.start({
      label: label.trim() || t('cooking.timers.defaultLabel', { minutes: mins }),
      durationSeconds: Math.round(mins * 60),
      recipeId: recipeId ?? null,
    });
    setLabel('');
    announce(t('cooking.voice.timerStarted', { minutes: mins }));
  };

  return (
    <div className={cn('space-y-3', className)} data-testid="timer-panel">
      {timers.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('cooking.timers.empty')}</p>
      ) : (
        <ul className="space-y-2">
          {timers.map((timer) => (
            <TimerRow key={timer.id} timer={timer} large={large} />
          ))}
        </ul>
      )}
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          start();
        }}
      >
        <div className="min-w-0 flex-1 basis-32">
          <Label htmlFor="timer-label" className="mb-1 block text-xs">
            {t('cooking.timers.label')}
          </Label>
          <Input
            id="timer-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            maxLength={120}
            className={large ? 'h-12 text-base' : undefined}
          />
        </div>
        <div className="w-24">
          <Label htmlFor="timer-minutes" className="mb-1 block text-xs">
            {t('cooking.timers.minutes')}
          </Label>
          <Input
            id="timer-minutes"
            type="number"
            inputMode="decimal"
            min={0.5}
            step={0.5}
            value={minutes}
            onChange={(e) => setMinutes(e.target.value)}
            className={large ? 'h-12 text-base' : undefined}
            data-testid="timer-minutes"
          />
        </div>
        <Button type="submit" size={large ? 'lg' : 'default'} data-testid="timer-start">
          <TimerIcon aria-hidden /> {t('cooking.timers.start')}
        </Button>
      </form>
      {permission === 'default' && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() =>
            void requestNotificationPermission().then(() => setPermission(notificationPermission()))
          }
        >
          <Bell aria-hidden /> {t('cooking.timers.enableNotifications')}
        </Button>
      )}
      {permission === 'denied' && (
        <p className="text-xs text-muted-foreground">{t('cooking.timers.notificationsDenied')}</p>
      )}
    </div>
  );
}
